'use client'

import { Button as HeroButton, ButtonProps as HeroButtonProps } from '@heroui/react'
import { forwardRef } from 'react'

import { Icon } from './icon'

export interface ButtonProps extends HeroButtonProps {}

/**
 * The raw @heroui/react Button silently drops `isLoading` and `spinner`:
 * ButtonRoot spreads both into react-aria's Button, which discards them, so
 * no spinner ever renders and the button stays clickable while "loading".
 * This wrapper implements the pending state for real. When `isLoading` is
 * truthy it renders a visible spinner (the caller's `spinner` node, or the
 * project spinner icon), keeps the label visible (unless `isIconOnly`, where
 * the spinner replaces the icon), disables the button so it dims and cannot
 * be re-clicked, and sets `aria-busy`. Neither `isLoading` nor `spinner`
 * reaches the underlying HeroUI Button.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ isLoading, isDisabled, isIconOnly, spinner, children, ...props }, ref) => {
    const spinnerNode = spinner ?? <Icon icon='spinner' className='animate-spin' aria-hidden='true' />
    const withSpinner = (resolved: React.ReactNode) => (
      <>
        {spinnerNode}
        {isIconOnly ? null : resolved}
      </>
    )

    const content: ButtonProps['children'] = !isLoading
      ? children
      : typeof children === 'function'
        ? (renderProps) => withSpinner(children(renderProps))
        : withSpinner(children)

    return (
      <HeroButton
        ref={ref}
        isDisabled={isLoading || isDisabled}
        isIconOnly={isIconOnly}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {content}
      </HeroButton>
    )
  }
)

Button.displayName = 'Button'

export default Button
